import React from "react";
import { FileSpreadsheet, Info } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Phase 5.5 — Reusable list of accepted file naming conventions per upload
 * context. Used in Upload, Catalog ingestion areas and the Schedule upload
 * dialog so users always see which files match which slot.
 *
 * Pattern: each row shows the canonical prefix (`GENDETAILED_*`) and a
 * one-line description. The user can also pass `highlight` (a filename) so
 * the row matching the file the user just dropped lights up.
 */

const FILES = {
  catalog: [
    { prefix: "IC338_*.xlsx", label: "IC338", desc: "Inventario actual (cantidad, costos, ubicaciones)." },
    { prefix: "SHOPIFY_*.csv", label: "Shopify CSV", desc: "Catálogo maestro (retail, vendor, imágenes)." },
    { prefix: "GENQTY_AVAIL_*.xlsx", label: "GENQTY AVAIL", desc: "Inventario disponible neto + fotos + serial numbers.", isNew: true },
  ],
  reports: [
    { prefix: "GENDETAILED_*.xlsx", label: "GENDETAILED", desc: "Transacciones RF PICK / STAGE / TRNS / RCPT / RCVR." },
    { prefix: "IC385DETAIL_*.xlsx", label: "IC385 detail", desc: "Movimientos de inventario por orden." },
    { prefix: "IC385P2DETAIL_*.xlsx", label: "IC385 P2", desc: "Movimientos As-Is (floor sample)." },
  ],
  timeclocks: [
    // Phase 9.5 — unified Time Clock ingest. All three legacy filename
    // patterns (TE350, Time Clock Yearly, Time Clock Month) share the same
    // schema, so we only advertise the canonical `TE350_*.xlsx` slot.
    { prefix: "TE350_*.xlsx", label: "Time Clock (TE350)", desc: "Horas trabajadas del ERP." },
  ],
  schedule: [
    { prefix: "Horario_*.xlsx", label: "Horario semanal", desc: "Una hoja por semana con secciones." },
  ],
  all: [
    { prefix: "GENDETAILED_*.xlsx", label: "GENDETAILED", desc: "Transacciones RF PICK / STAGE / TRNS / RCPT / RCVR." },
    { prefix: "IC385DETAIL_*.xlsx", label: "IC385 detail", desc: "Movimientos de inventario por orden." },
    { prefix: "IC385P2DETAIL_*.xlsx", label: "IC385 P2", desc: "Movimientos As-Is (floor sample)." },
    { prefix: "TE350_*.xlsx", label: "Time Clock (TE350)", desc: "Horas trabajadas del ERP." },
    { prefix: "IC338_*.xlsx", label: "IC338", desc: "Inventario actual de catálogo." },
    { prefix: "SHOPIFY_*.csv", label: "Shopify CSV", desc: "Catálogo maestro Shopify." },
    { prefix: "GENQTY_AVAIL_*.xlsx", label: "GENQTY AVAIL", desc: "Disponibilidad real + imágenes + serials.", isNew: true },
  ],
};

const PREFIX_MATCHERS = [
  { re: /^GENDETAILED/i, kind: "GENDETAILED" },
  { re: /^IC385P2/i, kind: "IC385P2DETAIL" },
  { re: /^IC385/i, kind: "IC385DETAIL" },
  // Phase 9.5 — Time Clock filenames all resolve to TE350 (unified path).
  { re: /^TE350|TIME[ _]?CLOCK/i, kind: "TE350" },
  { re: /^IC338/i, kind: "IC338" },
  { re: /^SHOPIFY|BASE[ _]?DE[ _]?SHOPIFY/i, kind: "Shopify CSV" },
  { re: /^GENQTY[ _]?AVAIL|^GENQTY/i, kind: "GENQTY AVAIL" },
  { re: /HORARIO|SCHEDULE/i, kind: "Horario" },
];

/**
 * Best-effort detection of the canonical label from a filename. Returns null
 * if nothing matches.
 */
export function detectKind(filename) {
  const up = (filename || "").toUpperCase();
  for (const m of PREFIX_MATCHERS) {
    if (m.re.test(up)) return m.kind;
  }
  return null;
}

/**
 * Returns true if `filename` looks plausible for `expectedKind`. Used by
 * upload UI to warn but not block.
 */
export function isLikelyKind(filename, expectedKind) {
  const detected = detectKind(filename);
  if (!detected || !expectedKind) return true; // unknown → allow
  return detected.toLowerCase().includes(expectedKind.toLowerCase()) ||
    expectedKind.toLowerCase().includes(detected.toLowerCase());
}

export default function AcceptedFileList({ type = "all", highlight, className }) {
  const items = FILES[type] || FILES.all;
  const highlightKind = highlight ? detectKind(highlight) : null;

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-muted/40 p-3 text-xs",
        className
      )}
      data-testid={`accepted-file-list-${type}`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Info className="h-3 w-3" />
        Archivos aceptados
      </div>
      <ul className="space-y-1">
        {items.map((it) => {
          const isMatch =
            highlightKind &&
            (it.label.toLowerCase().includes(highlightKind.toLowerCase()) ||
              highlightKind.toLowerCase().includes(it.label.toLowerCase()));
          return (
            <li
              key={it.prefix}
              data-testid={`accepted-file-${it.prefix}`}
              className={cn(
                "flex items-start gap-2 rounded px-1.5 py-1 transition-colors",
                isMatch && "bg-primary/10 ring-1 ring-primary/40"
              )}
            >
              <FileSpreadsheet
                className={cn(
                  "mt-0.5 h-3 w-3 shrink-0",
                  isMatch ? "text-primary" : "text-muted-foreground"
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <code className="font-mono text-[11px] font-medium">
                    {it.prefix}
                  </code>
                  {it.isNew && (
                    <span className="rounded-sm bg-emerald-500/15 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                      Nuevo
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">{it.desc}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
